import { MemberRoleTypes, SdbMemberTypes } from "@custom-types/member-types";
import { SdbTeamMemberBase } from "@custom-types/teams-types";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { sphereColors } from "@styles/common-colors";
import { getHigherRole } from "@utils/member-utils";
import { getPrettyName } from "@utils/user-utils";

/**
 * Maps team members to sdb members by calculating the higher role of the member
 * based on the team role and company role if companyMembersMap is provided
 */
export function mapTeamMembersToSdbMembers(
  members: SphereDashboardAPITypes.ITeamMember[],
  teamRole: MemberRoleTypes,
  companyMembers?: Map<string, SdbMemberTypes>
): SdbTeamMemberBase[] {
  return members.map((member) => {
    const companyRole = companyMembers?.get(member.userResponse.identity)?.role;
    return {
      ...member.userResponse,
      name: getPrettyName(member.userResponse),
      role: companyRole ? getHigherRole(teamRole, companyRole) : teamRole,
    };
  });
}

const categoryKindColorMap: Record<
  SphereDashboardAPITypes.TeamCategoryKind,
  string
> = {
  [SphereDashboardAPITypes.TeamCategoryKind.emailDomains]:
    sphereColors.purple400,
  [SphereDashboardAPITypes.TeamCategoryKind.allMembers]: sphereColors.gray700,
  [SphereDashboardAPITypes.TeamCategoryKind.default]: sphereColors.gray700,
};

/** Returns the default color used for a given team category kind. */
export function getColorByTeamCategoryKind(
  categoryKind: SphereDashboardAPITypes.TeamCategoryKind
): string {
  return categoryKindColorMap[categoryKind] ?? categoryKindColorMap.default;
}

const teamCategoryKindLabelMap: Partial<
  Record<SphereDashboardAPITypes.TeamCategoryKind, string>
> = {
  [SphereDashboardAPITypes.TeamCategoryKind.emailDomains]: "Auto-managed",
};

/** Returns the label to display in the chip */
export function getLabelByTeamCategoryKind(
  categoryKind: SphereDashboardAPITypes.TeamCategoryKind
): string | undefined {
  return teamCategoryKindLabelMap[categoryKind];
}

/** Checks whether the provided team category kind is `emailDomains`. */
export function isEmailDomainsTeam(
  categoryKind: SphereDashboardAPITypes.TeamCategoryKind
): boolean {
  return categoryKind === SphereDashboardAPITypes.TeamCategoryKind.emailDomains;
}
